import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import AdminLayout from "../../../layouts/admin/AdminLayout";
import '../../../assets/css/admin/dashboard/Dashboard.css';

const Dashboard = () => {

  // 1. STATES
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [activeBookings, setActiveBookings] = useState(0);
  const [revenue, setRevenue] = useState('PKR 0');
  const [upcomingEvents, setUpcomingEvents] = useState(0);
  const [recentBookings, setRecentBookings] = useState([]);
  const [topVenues, setTopVenues] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);

  const colors = ['#f472b6', '#2dd4bf', '#fbbf24', '#a78bfa'];

  // 2. FETCH FUNCTION (onClick/useEffect)
  const fetchDashboardStats = () => {
    axios.get('http://127.0.0.1:8000/api/admin/dashboard/stats')
      .then((res) => {
        if (res.data.success) {
          const data = res.data.data;
          setTotalCustomers(data.totalCustomers);
          setActiveBookings(data.activeBookings);
          setRevenue(data.revenue);
          setUpcomingEvents(data.upcomingEvents);
          setRecentBookings(data.recentBookings);
          setTopVenues(data.topVenues);
          setRecentPayments(data.recentPayments);
          setRecentTickets(data.recentTickets);
        }
      })
      .catch((err) => {
        console.error('Dashboard stats error:', err);
      });
  };

  // 3. useEffect on mount
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const statCards = [
    {
      icon: 'bi bi-people-fill',
      iconBg: '#f472b6',
      label: 'Total Customers',
      value: totalCustomers,
      change: '+12%',
    },
    {
      icon: 'bi bi-calendar-check-fill',
      iconBg: '#2dd4bf',
      label: 'Active Bookings',
      value: activeBookings,
      change: '+8%',
    },
    {
      icon: 'bi bi-currency-dollar',
      iconBg: '#fbbf24',
      label: 'Revenue (Mo)',
      value: revenue,
      change: '+24%',
    },
    {
      icon: 'bi bi-calendar-event-fill',
      iconBg: '#a78bfa',
      label: 'Upcoming Events',
      value: upcomingEvents,
      change: '+5%',
    },
  ];

  const maxCount = topVenues.length > 0 ? Math.max(...topVenues.map(v => v.count)) : 1;

  // Helper to style payment/ticket status badges
  const getStatusClass = (status) => {
    if (!status) return 'dash-badge-default';
    const s = status.toLowerCase();
    if (s === 'paid' || s === 'resolved' || s === 'completed') return 'dash-badge-success';
    if (s === 'pending' || s === 'open') return 'dash-badge-pending';
    if (s === 'failed' || s === 'closed') return 'dash-badge-danger';
    return 'dash-badge-default';
  };

  return (
    <AdminLayout>
      <div className="dash-wrapper">
        <Container fluid>

          {/* WELCOME BANNER */}
          <div className="dash-banner">
            <div className="dash-banner-icon">✦</div>
            <h2 className="dash-banner-title">Welcome back, Admin</h2>
            <p className="dash-banner-sub">Here's what's happening across Bridely today.</p>
          </div>

          {/* STAT CARDS */}
          <Row className="g-3 mb-4">
            {statCards.map((card, i) => (
              <Col xs={12} sm={6} xl={3} key={i}>
                <div className="dash-stat-card">
                  <div className="dash-stat-top">
                    <div className="dash-stat-icon" style={{ background: card.iconBg }}>
                      <i className={card.icon}></i>
                    </div>
                    <span className="dash-stat-change">↗ {card.change}</span>
                  </div>
                  <p className="dash-stat-label">{card.label}</p>
                  <h3 className="dash-stat-value">{card.value}</h3>
                </div>
              </Col>
            ))}
          </Row>

          {/* SECOND ROW: BOOKINGS + TOP VENUES */}
          <Row className="g-3 mb-3">

            {/* RECENT BOOKINGS */}
            <Col xs={12} lg={6}>
              <div className="dash-card">
                <h5 className="dash-card-title">Recent Bookings</h5>
                <div className="dash-bookings-list">
                  {recentBookings.map((b, i) => (
                    <div className="dash-booking-item" key={i}>
                      <div className="dash-booking-avatar" style={{ background: colors[i % colors.length] }}>
                        {b.initial}
                      </div>
                      <div className="dash-booking-info">
                        <p className="dash-booking-name">{b.name}</p>
                        <p className="dash-booking-venue">{b.venue}</p>
                      </div>
                      <span className="dash-booking-amount">{b.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            {/* TOP VENUES */}
            <Col xs={12} lg={6}>
              <div className="dash-card">
                <h5 className="dash-card-title">Top Venues</h5>
                <div className="dash-venues-list">
                  {topVenues.map((v, i) => (
                    <div className="dash-venue-item" key={i}>
                      <div className="dash-venue-top">
                        <span className="dash-venue-name">{v.name}</span>
                        <span className="dash-venue-count">{v.count}</span>
                      </div>
                      <div className="dash-venue-bar-track">
                        <div
                          className="dash-venue-bar-fill"
                          style={{ width: `${(v.count / maxCount) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

          </Row>

          {/* THIRD ROW: RECENT PAYMENTS + RECENT TICKETS */}
          <Row className="g-3">

            {/* RECENT PAYMENTS */}
            <Col xs={12} lg={6}>
              <div className="dash-card">
                <h5 className="dash-card-title">Recent Payments</h5>
                <div className="dash-bookings-list">
                  {recentPayments.map((p, i) => (
                    <div className="dash-booking-item" key={i}>
                      <div className="dash-booking-avatar" style={{ background: colors[i % colors.length] }}>
                        {p.initial}
                      </div>
                      <div className="dash-booking-info">
                        <p className="dash-booking-name">{p.name}</p>
                        <p className="dash-booking-venue">{p.venue}</p>
                      </div>
                      <div className="dash-payment-right">
                        <span className="dash-booking-amount">{p.amount}</span>
                        <span className={`dash-badge ${getStatusClass(p.status)}`}>{p.status}</span>
                      </div>
                    </div>
                  ))}
                  {recentPayments.length === 0 && (
                    <p className="dash-empty-text">No recent payments.</p>
                  )}
                </div>
              </div>
            </Col>

            {/* RECENT TICKETS */}
            <Col xs={12} lg={6}>
              <div className="dash-card">
                <h5 className="dash-card-title">Recent Tickets</h5>
                <div className="dash-bookings-list">
                  {recentTickets.map((t, i) => (
                    <div className="dash-booking-item" key={i}>
                      <div className="dash-booking-avatar" style={{ background: colors[i % colors.length] }}>
                        {t.initial}
                      </div>
                      <div className="dash-booking-info">
                        <p className="dash-booking-name">{t.name}</p>
                        <p className="dash-booking-venue">{t.subject}</p>
                      </div>
                      <span className={`dash-badge ${getStatusClass(t.status)}`}>{t.status}</span>
                    </div>
                  ))}
                  {recentTickets.length === 0 && (
                    <p className="dash-empty-text">No recent tickets.</p>
                  )}
                </div>
              </div>
            </Col>

          </Row>

        </Container>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;